import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Settings, 
  BookOpen,
  Trash2,
  Edit,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import { evaluationFrameworksApi } from '../services/api';
import { 
  FrameworkSummary, 
  EvaluationFramework, 
  FrameworkWithMetrics
} from '../types';
import FrameworkModal from './FrameworkModal';
import FrameworkDetailsView from './FrameworkDetailsView';

interface EvaluationFrameworksProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const EvaluationFrameworks: React.FC<EvaluationFrameworksProps> = ({ addNotification }) => {
  const [frameworks, setFrameworks] = useState<FrameworkSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [frameworkTypes, setFrameworkTypes] = useState<string[]>([]);
  const [frameworkStatuses, setFrameworkStatuses] = useState<string[]>([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFramework, setEditingFramework] = useState<EvaluationFramework | null>(null);

  // Tab: 'list' = frameworks list, 'details' = selected framework details
  const [activeTab, setActiveTab] = useState<'list' | 'details'>('list');
  const [selectedFramework, setSelectedFramework] = useState<FrameworkWithMetrics | null>(null);

  useEffect(() => {
    loadFrameworks();
    loadMetadata();
  }, []);

  const loadFrameworks = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedType) params.framework_type = selectedType;
      if (selectedStatus) params.status = selectedStatus;
      
      const response = await evaluationFrameworksApi.getAllFrameworks(params);
      setFrameworks(response.frameworks);
    } catch (error) {
      console.error('Error loading frameworks:', error);
      addNotification('Failed to load evaluation frameworks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadMetadata = async () => {
    try {
      const [types, statuses] = await Promise.all([
        evaluationFrameworksApi.getFrameworkTypes(),
        evaluationFrameworksApi.getFrameworkStatuses()
      ]);
      setFrameworkTypes(types);
      setFrameworkStatuses(statuses);
    } catch (error) {
      console.error('Error loading metadata:', error);
    }
  };

  const handleSearch = () => {
    loadFrameworks();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedType('');
    setSelectedStatus('');
    loadFrameworks();
  };

  const handleViewDetails = async (framework: FrameworkSummary) => {
    try {
      const details = await evaluationFrameworksApi.getFrameworkWithMetrics(framework.id);
      setSelectedFramework(details);
      setActiveTab('details');
    } catch (error) {
      console.error('Error loading framework details:', error);
      addNotification('Failed to load framework details', 'error');
    }
  };

  const handleEditFramework = async (framework: FrameworkSummary) => {
    try {
      const details = await evaluationFrameworksApi.getFramework(framework.id);
      setEditingFramework(details);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error loading framework for edit:', error);
      addNotification('Failed to load framework details', 'error');
    }
  };

  const handleDeleteFramework = (framework: FrameworkSummary) => {
    setSelectedFramework(framework as FrameworkWithMetrics);
    setShowDeleteModal(true);
  };

  const confirmDeleteFramework = async () => {
    if (!selectedFramework) return;
    
    try {
      await evaluationFrameworksApi.deleteFramework(selectedFramework.id);
      addNotification('Framework deleted successfully', 'success');
      setShowDeleteModal(false);
      setSelectedFramework(null);
      setActiveTab('list');
      loadFrameworks();
    } catch (error) {
      console.error('Error deleting framework:', error);
      addNotification('Failed to delete framework', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'deprecated': return 'text-red-600 bg-red-100';
      case 'beta': return 'text-yellow-600 bg-yellow-100';
      case 'maintenance': return 'text-blue-600 bg-blue-100';
      default: return 'text-slate-300 bg-slate-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'deprecated': return <AlertCircle className="h-4 w-4" />;
      case 'beta': return <Clock className="h-4 w-4" />;
      case 'maintenance': return <Settings className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ragas': return 'text-purple-600 bg-purple-100';
      case 'openai': return 'text-blue-600 bg-blue-100';
      case 'trulens': return 'text-green-600 bg-green-100';
      case 'langchain': return 'text-orange-600 bg-orange-100';
      case 'custom': return 'text-slate-300 bg-slate-600';
      default: return 'text-slate-300 bg-slate-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Evaluation Frameworks</h1>
            <p className="text-slate-300 mt-1">Manage evaluation frameworks and their metrics</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Framework
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Search</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search frameworks..."
                className="pl-10 w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Types</option>
              {frameworkTypes.map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Statuses</option>
              {frameworkStatuses.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end space-x-2">
            <button
              onClick={handleSearch}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabbed content: Frameworks list | Framework details */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        {/* Tab bar */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'list'
                ? 'border-indigo-500 text-indigo-300'
                : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
            }`}
          >
            Frameworks {frameworks.length > 0 && `(${frameworks.length})`}
          </button>
          {selectedFramework && (
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-300'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
              }`}
            >
              {selectedFramework.name}
            </button>
          )}
        </div>

        {/* Tab panels */}
        {activeTab === 'list' && (
          <>
            {loading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-2 text-slate-400">Loading frameworks...</p>
              </div>
            ) : frameworks.length === 0 ? (
              <div className="p-6 text-center">
                <Settings className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-100 mb-2">No frameworks found</h3>
                <p className="text-slate-400 mb-4">Get started by creating your first evaluation framework.</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Framework
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-700">
                  <thead className="bg-slate-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Version</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Metrics</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-800 divide-y divide-slate-700">
                    {frameworks.map((framework) => (
                      <tr key={framework.id} className="hover:bg-slate-700/30">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-slate-100">{framework.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(framework.framework_type)}`}>
                            {framework.framework_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(framework.status)}`}>
                            {getStatusIcon(framework.status)}
                            <span className="ml-1">{framework.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                          {framework.version || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                          {framework.metrics_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewDetails(framework)}
                              className="inline-flex items-center px-2 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </button>
                            <button
                              onClick={() => handleEditFramework(framework)}
                              className="inline-flex items-center px-2 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteFramework(framework)}
                              className="inline-flex items-center px-2 py-1 border border-red-800 text-xs font-medium rounded text-red-200 bg-red-900/30 hover:bg-red-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'details' && selectedFramework && (
          <div className="p-6">
            <FrameworkDetailsView
              framework={selectedFramework}
              onClose={() => {
                setActiveTab('list');
                setSelectedFramework(null);
              }}
              onFrameworkUpdate={loadFrameworks}
              addNotification={addNotification}
              variant="inline"
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <FrameworkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={loadFrameworks}
        addNotification={addNotification}
      />

      <FrameworkModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={loadFrameworks}
        editingFramework={editingFramework}
        addNotification={addNotification}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedFramework && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-100 mt-4">Delete Framework</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-slate-400">
                  Are you sure you want to delete "{selectedFramework.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedFramework(null);
                  }}
                  className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFramework}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluationFrameworks;
